const html = require('choo/html');
const Component = require('choo/component');
const assets = require('../../common/assets');

class IntroGlobe extends Component {
  constructor(id, state, emit) {
    super(id);
    this.state = state;
    this.emit = emit;
    this.globe = null;
    this.globeInitialized = false;
    this.isAutoRotating = true;
    this.resizeObserver = null;
  }

  update() {
    return false;
  }

  load(element) {
    this.initGlobe(element);
  }

  unload() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.globe && typeof this.globe._destructor === 'function') {
      try {
        this.globe._destructor();
      } catch (e) {}
    }
    this.globe = null;
    this.globeInitialized = false;
  }

  initGlobe(element) {
    if (this.globeInitialized) return;
    const container = element.querySelector('#gis-3d-globe');
    if (!container) return;

    const tryInit = () => {
      if (typeof window !== 'undefined' && typeof window.Globe === 'function') {
        this.renderGlobe(container, element);
        return true;
      }
      return false;
    };

    if (!tryInit()) {
      let retries = 0;
      const timer = setInterval(() => {
        retries++;
        if (tryInit() || retries > 30) {
          clearInterval(timer);
        }
      }, 100);
    }
  }

  renderGlobe(container, rootEl) {
    if (this.globeInitialized) return;
    this.globeInitialized = true;

    container.innerHTML = '';

    const width = container.clientWidth || 420;
    const height = container.clientHeight || 340;
    const minDim = Math.min(width, height);
    const optimalAltitude = Math.max(1.8, 2.05 * (440 / Math.max(200, minDim)));

    const VN_LAT = 16.05;
    const VN_LNG = 108.20;

    const ringsData = [
      { lat: 21.0285, lng: 105.8542, maxR: 4.5, propagationSpeed: 2.2, repeatPeriod: 1200, color: () => '#dc2626' },
      { lat: 16.0544, lng: 108.2022, maxR: 6.5, propagationSpeed: 2.6, repeatPeriod: 1000, color: () => '#ef4444' },
      { lat: 10.8231, lng: 106.6297, maxR: 5.0, propagationSpeed: 2.2, repeatPeriod: 1100, color: () => '#dc2626' },
      { lat: 16.5, lng: 112.0, maxR: 4.0, propagationSpeed: 1.8, repeatPeriod: 1400, color: () => '#f59e0b' },
      { lat: 9.0, lng: 114.0, maxR: 4.0, propagationSpeed: 1.8, repeatPeriod: 1400, color: () => '#f59e0b' }
    ];

    const arcsData = [
      { startLat: VN_LAT, startLng: VN_LNG, endLat: 35.6762, endLng: 139.6503, color: ['#ef4444', '#f87171'] },
      { startLat: VN_LAT, startLng: VN_LNG, endLat: 1.3521, endLng: 103.8198, color: ['#ef4444', '#fbbf24'] },
      { startLat: VN_LAT, startLng: VN_LNG, endLat: 50.1109, endLng: 8.6821, color: ['#ef4444', '#f87171'] },
      { startLat: VN_LAT, startLng: VN_LNG, endLat: 37.7749, endLng: -122.4194, color: ['#ef4444', '#60a5fa'] },
      { startLat: VN_LAT, startLng: VN_LNG, endLat: -33.8688, endLng: 151.2093, color: ['#ef4444', '#34d399'] }
    ];

    const pointsData = [
      { lat: 21.0285, lng: 105.8542, size: 0.4, color: '#dc2626', name: 'Hà Nội' },
      { lat: 16.0544, lng: 108.2022, size: 0.6, color: '#ef4444', name: 'Đà Nẵng (Tâm VN2000)' },
      { lat: 10.8231, lng: 106.6297, size: 0.4, color: '#dc2626', name: 'TP. Hồ Chí Minh' },
      { lat: 16.5, lng: 112.0, size: 0.35, color: '#f59e0b', name: 'Hoàng Sa (Việt Nam)' },
      { lat: 9.0, lng: 114.0, size: 0.35, color: '#f59e0b', name: 'Trường Sa (Việt Nam)' },
      { lat: 35.6762, lng: 139.6503, size: 0.25, color: '#94a3b8', name: 'Tokyo' },
      { lat: 1.3521, lng: 103.8198, size: 0.25, color: '#94a3b8', name: 'Singapore' },
      { lat: 50.1109, lng: 8.6821, size: 0.25, color: '#94a3b8', name: 'Frankfurt' },
      { lat: 37.7749, lng: -122.4194, size: 0.25, color: '#94a3b8', name: 'San Francisco' },
      { lat: -33.8688, lng: 151.2093, size: 0.25, color: '#94a3b8', name: 'Sydney' }
    ];

    try {
      this.globe = window.Globe()
        .width(width)
        .height(height)
        .globeImageUrl('/8k_earth_daymap.jpg')
        .backgroundImageUrl('/night-sky.png')
        .showAtmosphere(true)
        .atmosphereColor('#00d2ff')
        .atmosphereAltitude(0.24)
        .ringsData(ringsData)
        .ringColor(d => d.color)
        .ringMaxRadius(d => d.maxR)
        .ringPropagationSpeed(d => d.propagationSpeed)
        .ringRepeatPeriod(d => d.repeatPeriod)
        .arcsData(arcsData)
        .arcColor(d => d.color)
        .arcDashLength(0.4)
        .arcDashGap(0.2)
        .arcDashAnimateTime(1600)
        .arcAltitude(0.22)
        .pointsData(pointsData)
        .pointColor(d => d.color)
        .pointAltitude(0.01)
        .pointRadius(d => d.size)
        .pointLabel(d => d.name)
        (container);

      // Balanced daylight lighting: Crisp sunlight with natural contrast and depth
      const applyDaylight = () => {
        const scene = this.globe ? this.globe.scene() : null;
        if (scene) {
          scene.traverse(obj => {
            if (obj.isAmbientLight) {
              // Moderate ambient light so shadows & deep ocean colors are not washed out into grey
              obj.intensity = 1.1;
              if (obj.color) obj.color.set(0xffffff);
            }
            if (obj.isDirectionalLight) {
              // Strong focused sunlight giving radiant daylight brilliance to land and water specular glint
              obj.intensity = 2.4;
              if (obj.color) obj.color.set(0xffffff);
              if (obj.position) obj.position.set(1.5, 1, 1.2).normalize();
            }
          });
        }
      };

      // High-DPI rendering support
      const renderer = this.globe.renderer ? this.globe.renderer() : null;
      if (renderer) {
        renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2.5));
        if (typeof renderer.toneMappingExposure === 'number') {
          renderer.toneMappingExposure = 1.0;
        }
      }

      this.globe.onGlobeReady(() => {
        applyDaylight();
        const globeMaterial = this.globe.globeMaterial();
        if (globeMaterial) {
          if (globeMaterial.color) globeMaterial.color.set(0xffffff);
          // Glossy ocean sheen catching directional sunlight
          globeMaterial.roughness = 0.45;
          globeMaterial.metalness = 0.05;
          // Clean emissive: subtle pure deep-blue tint, no flat grey wash
          if (globeMaterial.emissive && globeMaterial.emissive.set) {
            globeMaterial.emissive.set(0x000818);
          }
          if (globeMaterial.emissiveMap) {
            globeMaterial.emissiveMap = null;
          }
          if (globeMaterial.map && renderer) {
            globeMaterial.map.anisotropy = renderer.capabilities.getMaxAnisotropy();
            globeMaterial.map.needsUpdate = true;
          }
        }

        // Apply high-vibrancy filter to canvas for rich royal blue oceans & lush greens
        const canvas = container.querySelector('canvas');
        if (canvas) {
          canvas.style.filter = 'saturate(1.42) contrast(1.12) brightness(1.06)';
        }
      });

      // Controls: auto-rotate smoothly and allow zoom
      if (this.globe.controls) {
        const controls = this.globe.controls();
        if (controls) {
          controls.autoRotate = true;
          controls.autoRotateSpeed = 0.55;
          controls.enableZoom = true;
          controls.minDistance = 120;
          controls.maxDistance = 500;
        }
      }

      // Start view focused on Vietnam
      this.globe.pointOfView({ lat: VN_LAT, lng: VN_LNG, altitude: optimalAltitude });

      // Periodically reinforce daylight lighting
      setTimeout(applyDaylight, 300);
      setTimeout(applyDaylight, 800);
      setTimeout(applyDaylight, 1500);

      // Auto-resize observer: adapts globe size and centers sphere when container flexes
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(entries => {
          for (const entry of entries) {
            if (this.globe && entry.contentRect.width > 0 && entry.contentRect.height > 0) {
              const w = Math.floor(entry.contentRect.width);
              const h = Math.floor(entry.contentRect.height);
              this.globe.width(w);
              this.globe.height(h);
              const mDim = Math.min(w, h);
              const altitude = Math.max(1.8, 2.05 * (440 / Math.max(200, mDim)));
              this.globe.pointOfView({ lat: VN_LAT, lng: VN_LNG, altitude: altitude }, 150);
            }
          }
        });
        this.resizeObserver.observe(container);
      }
    } catch (e) {
      console.error('Failed to init 3D globe:', e);
    }
  }

  createElement() {
    return html`
      <send-intro
        class="flex flex-col items-center justify-between bg-white px-0 py-0 mb-0 h-full w-full dark:bg-grey-90"
      >
        <div class="flex flex-col h-full w-full justify-between">
          <div class="pt-0 pb-2 px-3 flex-shrink-0">
            <div class="flex flex-col space-y-2 mb-1">
              <div class="flex items-start">
                <span class="icon-feat-lock"></span>
                <span class="text-gray-700 leading-snug" style="font-size: 14px;">Mã hóa đầu cuối, chỉ người nhận mới có thể truy cập.</span>
              </div>
              <div class="flex items-start">
                <span class="icon-feat-expire"></span>
                <span class="text-gray-700 leading-snug" style="font-size: 14px;">Quyền truy cập tự động hết hiệu lực theo thời hạn chia sẻ.</span>
              </div>
              <div class="flex items-start">
                <span class="icon-feat-shield"></span>
                <span class="text-gray-700 leading-snug" style="font-size: 14px;"><span class="font-bold" style="color: red;">gis.vn</span> không lưu trữ, không xem được dữ liệu.</span>
              </div>
            </div>
          </div>

          <!-- 3D Earth Globe Section: Tự co giãn theo mép trái, góc bo mượt mà không răng cưa -->
          <div class="relative flex flex-col items-center justify-end flex-1 w-full min-h-0 px-0 pb-0">
            <style>
              #gis-3d-globe canvas {
                filter: saturate(1.42) contrast(1.12) brightness(1.06);
              }
            </style>
            <div
              id="gis-3d-globe"
              class="w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
              style="width: 100%; height: 100%; min-height: 260px; position: relative; border-radius: 14px; overflow: hidden; background-color: #020617; transform: translateZ(0); -webkit-mask-image: -webkit-radial-gradient(white, black); mask-image: -webkit-radial-gradient(white, black); box-shadow: 0 0 0 1px #020617; margin-top: auto; margin-bottom: 0;"
            >
              <div class="flex flex-col items-center justify-center text-gray-400 text-xs py-20">
                <div class="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                <span class="text-xs font-semibold" style="color: #38bdf8;">Đang tải quả địa cầu 3D...</span>
              </div>
            </div>
          </div>
        </div>
      </send-intro>
    `;
  }
}

module.exports = function intro(state, emit) {
  return state.cache(IntroGlobe, 'intro-3d-globe').render();
};
